import { Button, Flex, Text } from '@byecode/ui'
import { SpaceVersion } from '@lighthouse/core'
import { spaceGradeCodeMap, spaceGradeNameMap, useAtomData } from '@lighthouse/shared'
import { getFileSizeWithUnit } from '@lighthouse/tools'
import { Anchor } from '@mantine/core'
import { find } from 'rambda'
import React from 'react'
import useSWR from 'swr'

import { currentAppOfSpaceIdAtom } from '@/atoms/application/state'
import { useCurrentSpaceID, useWorkSpace } from '@/hooks/useWorkSpace'
import * as srv from '@/services'

import { ALL_SPACE_VERSION } from '../constants'
import { openSaleSpaceGrade } from '../event'
import * as SC from './List.style'
import { formatSpaceFeaturesValue, formatVideoQualityValue } from './utils'

interface Props {
    onWechatPaySpaceUpgrade: (level: SpaceVersion) => void
}

/** 升级空间版本 */
export const List: React.FC<Props> = ({ onWechatPaySpaceUpgrade }) => {
    const appOfSpaceId = useAtomData(currentAppOfSpaceIdAtom)
    const currentSpace = useWorkSpace()
    const currentSpaceId = currentSpace?.id || appOfSpaceId

    const currentVersionCode = spaceGradeCodeMap[currentSpace.version ?? SpaceVersion.FREE]

    const { data } = useSWR(
        `/space/getSpacePlans-${currentSpaceId}`,
        () => {
            if (!currentSpaceId) {
                return
            }
            return srv.getSpacePlans(currentSpaceId)
        },
        {
            revalidateOnFocus: false,
            keepPreviousData: true
        }
    )

    return (
        <>
            <SC.Title>升级你的版本</SC.Title>
            <SC.Describe>
                如有任何问题，请
                <Anchor target="_target" onClick={openSaleSpaceGrade}>
                    咨询工作人员
                </Anchor>
                ，服务时段（周一至周日 9:00~20:00）
            </SC.Describe>
            <SC.BlockContainer>
                {ALL_SPACE_VERSION.map((item, index) => {
                    const plan = find(p => p.spacePlan === item.version, data || [])
                    const isPromo = !!plan && plan.originalPrice !== plan.realPrice
                    return (
                        <SC.Block key={item.version}>
                            <SC.BlockWrapper data-space-version={item.version}>
                                {/* {spaceThemeMap[item.version].image && <SC.VersionImage src={spaceThemeMap[item.version].image} />} */}
                                <SC.BlockContent>
                                    <Flex gap={8} alignItems="center">
                                        <SC.BlockTitle>{spaceGradeNameMap[item.version]}</SC.BlockTitle>
                                        {spaceGradeCodeMap[item.version] === currentVersionCode && (
                                            <SC.BlockCurrentTag>当前版本</SC.BlockCurrentTag>
                                        )}
                                    </Flex>
                                    <SC.BlockDescribe>{item.describe}</SC.BlockDescribe>
                                    <SC.BlockPrice>
                                        <Flex alignItems="flex-end">
                                            <Text size={20} color="var(--color-theme-7)">
                                                ￥
                                            </Text>
                                            <Text size={40} weight={500} style={{ lineHeight: 1 }} color="var(--color-theme-7)">
                                                {isPromo
                                                    ? formatSpaceFeaturesValue(plan.realPrice).value
                                                    : formatSpaceFeaturesValue(item.price).value}
                                            </Text>
                                        </Flex>
                                        {isPromo ? (
                                            <Text
                                                size={12}
                                                lineClamp={1}
                                                style={isPromo ? { textDecoration: 'line-through' } : {}}
                                                color="var(--color-gray-400)"
                                            >
                                                {plan?.originalPrice}/每年
                                            </Text>
                                        ) : (
                                            <Text size={12} lineClamp={1} color="var(--color-gray-400)">
                                                /每年
                                            </Text>
                                        )}
                                    </SC.BlockPrice>
                                    {(currentVersionCode ?? 0) <= spaceGradeCodeMap[item.version] &&
                                        spaceGradeCodeMap[item.version] !== 0 &&
                                        index < 6 && (
                                            <Button
                                                style={{
                                                    height: 48,
                                                    fontSize: 20,
                                                    fontWeight: 500,
                                                    color: item.version === SpaceVersion.ADVANCED ? '#fff' : 'var(--color-black)',
                                                    background:
                                                        item.version === SpaceVersion.ADVANCED
                                                            ? 'linear-gradient(91deg, #5551FF 4%, #7E51FF 97.52%)'
                                                            : 'var(--color-gray-100)',
                                                    boxShadow:
                                                        item.version === SpaceVersion.ADVANCED
                                                            ? '0px 8px 16px 0px rgba(95, 82, 255, 0.32)'
                                                            : undefined
                                                }}
                                                // icon={<SC.Icon type="WeChatLogo" size={24} color="#fff" />}
                                                onClick={() => onWechatPaySpaceUpgrade(item.version)}
                                                radius={12}
                                                type="primary"
                                                block
                                            >
                                                {currentVersionCode === index ? '续费' : '立即购买'}
                                            </Button>
                                        )}

                                    {currentVersionCode === index && index === 0 && (
                                        <Button style={{ height: 48, fontSize: 20, pointerEvents: 'none' }} radius={12} type="tag" block>
                                            当前版本
                                        </Button>
                                    )}

                                    {(currentVersionCode ?? 0) > index && (
                                        <Button style={{ height: 48, fontSize: 20, pointerEvents: 'none' }} radius={12} type="tag" block>
                                            已升更高版
                                        </Button>
                                    )}
                                    <SC.KindBlock>
                                        <SC.InfoList>
                                            <Text weight={600}>核心资源用量</Text>
                                            <Flex justifyContent="space-between">
                                                <Text color="var(--color-gray-500)">登录用户数</Text>
                                                <Text weight={600} color="var(--color-gray-900)">
                                                    {item.externalUserLimit > 0 ? item.externalUserLimit : '不限'}
                                                </Text>
                                            </Flex>
                                            <Flex justifyContent="space-between">
                                                <Text color="var(--color-gray-500)">访问流量</Text>
                                                <Text weight={600}>{getFileSizeWithUnit(item.accessFlow, 'MB')} / 年</Text>
                                            </Flex>
                                            <Flex justifyContent="space-between">
                                                <Text color="var(--color-gray-500)">附件储存</Text>
                                                <Text weight={600}>{getFileSizeWithUnit(item.storageLimit, 'MB')}</Text>
                                            </Flex>
                                            <Flex justifyContent="space-between">
                                                <Text color="var(--color-gray-500)">数据表总记录数</Text>
                                                <Text weight={600}>
                                                    {item.recordsLimit > 9999 ? item.recordsLimit?.toLocaleString() : item.recordsLimit}
                                                </Text>
                                            </Flex>
                                            <Flex justifyContent="space-between">
                                                <Text color="var(--color-gray-500)">视频清晰度</Text>
                                                <Text weight={600}>{formatVideoQualityValue(item.videoQuality)}</Text>
                                            </Flex>
                                            <Flex justifyContent="space-between">
                                                <Text color="var(--color-gray-500)">单个附件大小</Text>
                                                <Text weight={600}>{getFileSizeWithUnit(item.singleFileSizeLimit, 'B')}</Text>
                                            </Flex>

                                            {/* <Flex justifyContent="space-between">
                                                <Text color="var(--color-gray-500)">ByeCode验证码短信数量</Text>
                                                <Text weight={600}>{item.smsLimit === 0 ? '-' : item.smsLimit}</Text>
                                            </Flex> */}

                                            <Flex justifyContent="space-between">
                                                <Text color="var(--color-gray-500)">访客数量</Text>
                                                <Text weight={600}>
                                                    {item.accessUserLimit === -1 ? '不限' : `${item.accessUserLimit?.toLocaleString()}/月`}
                                                </Text>
                                            </Flex>
                                        </SC.InfoList>
                                    </SC.KindBlock>
                                    <SC.KindBlock>
                                        <SC.InfoList style={{ gap: 12 }}>
                                            <Text weight={600}>
                                                {item.coreVersion ? `${spaceGradeNameMap[item.coreVersion]}所有功能，以及：` : '核心功能'}
                                            </Text>
                                            {item.coreFunction.map(label => (
                                                <Flex key={label} gap={4}>
                                                    <SC.Icon type="Tick" color="var(--color-theme-7)" />
                                                    <Text color="var(--color-gray-500)">{label}</Text>
                                                </Flex>
                                            ))}
                                        </SC.InfoList>
                                    </SC.KindBlock>
                                </SC.BlockContent>
                            </SC.BlockWrapper>
                        </SC.Block>
                    )
                })}

                <SC.Block style={{ background: 'linear-gradient(180deg, #FDF0E6 0%, rgba(253, 240, 230, 0.08) 100%)' }}>
                    <SC.BlockWrapper>
                        {/* {spaceThemeMap[item.version].image && <SC.VersionImage src={spaceThemeMap[item.version].image} />} */}
                        <SC.BlockContent>
                            <SC.BlockTitle>旗舰版</SC.BlockTitle>
                            <SC.BlockDescribe>大规模数字化运营</SC.BlockDescribe>
                            <SC.BlockPrice>
                                <Text size={48} weight={500} style={{ lineHeight: 1 }} color="var(--color-theme-7)">
                                    咨询报价
                                </Text>
                            </SC.BlockPrice>
                            <Button
                                style={{
                                    height: 48,
                                    fontSize: 20,
                                    backgroundColor: '#000',
                                    boxShadow: '0px 8px 16px 0px rgba(16, 24, 40, 0.12)'
                                }}
                                icon={<SC.Icon type="PropertyPhone" size={24} color="#fff" />}
                                type="primary"
                                radius={12}
                                block
                                onClick={() => {
                                    srv.getUserConsult()
                                    openSaleSpaceGrade()
                                }}
                            >
                                联系我们
                            </Button>
                            <SC.KindBlock>
                                <SC.InfoList>
                                    <Text weight={600}>核心资源用量</Text>
                                    <Flex justifyContent="space-between">
                                        <Text color="var(--color-gray-500)">登录用户数</Text>
                                        <Text weight={600}>不限</Text>
                                    </Flex>
                                    <Flex justifyContent="space-between">
                                        <Text color="var(--color-gray-500)">访问流量</Text>
                                        <Text color="var(--color-gray-400)">咨询</Text>
                                    </Flex>
                                    <Flex justifyContent="space-between">
                                        <Text color="var(--color-gray-500)">附件存储</Text>
                                        <Text color="var(--color-gray-400)">咨询</Text>
                                    </Flex>
                                    <Flex justifyContent="space-between">
                                        <Text color="var(--color-gray-500)">数据表总记录数</Text>
                                        <Text weight={600}>不限</Text>
                                    </Flex>
                                    {/* <Flex justifyContent="space-between">
                                        <Text color="var(--color-gray-500)">ByeCode验证码短信数量</Text>
                                        <Text color="var(--color-gray-400)">咨询</Text>
                                    </Flex> */}
                                    <Flex justifyContent="space-between">
                                        <Text color="var(--color-gray-500)">访客数量</Text>
                                        <Text weight={600}>不限</Text>
                                    </Flex>
                                </SC.InfoList>
                            </SC.KindBlock>
                            <SC.KindBlock>
                                <SC.InfoList style={{ gap: 12 }}>
                                    <Text weight={600}>专业版中的所有功能，以及：</Text>
                                    <Flex gap={4}>
                                        <SC.Icon type="Tick" color="var(--color-theme-7)" />
                                        <Text color="var(--color-gray-500)">运行日志保留时长2年</Text>
                                    </Flex>
                                    <Flex gap={4}>
                                        <SC.Icon type="Tick" color="var(--color-theme-7)" />
                                        <Text color="var(--color-gray-500)">不限应用发布数</Text>
                                    </Flex>
                                    <Flex gap={4}>
                                        <SC.Icon type="Tick" color="var(--color-theme-7)" />
                                        <Text color="var(--color-gray-500)">优先开发新功能</Text>
                                    </Flex>
                                </SC.InfoList>
                            </SC.KindBlock>
                        </SC.BlockContent>
                    </SC.BlockWrapper>
                </SC.Block>
            </SC.BlockContainer>
        </>
    )
}
